<style scoped>
    .f-width {
        max-width:auto;
        white-space:nowrap;
    }
    td.rm-p-m {
        margin: 0px !important;
        padding: 0px !important;
    }
    td.meta-desc-w {
        width: auto;
    }
    .fix-w {
        margin-left: 22px !important;
    }
    .m-desc {
        color: #a97f28;
    }
</style>

<template>
    <div class="p-3" color="light">

        <v-progress-linear
            v-if="isLoading"
            indeterminate
            color="primary"
            class="mb-5">
        </v-progress-linear>

        <div v-else>

            <div class="mt-3 d-flex">
                <div>
                    <h5 class="subtitle-2">
                        Integration Meta Configs
                    </h5>
                    <div v-if="hasAuthenticate" class="mt-5">
                        <v-btn v-if="hasOauth2" small :disabled="isAuthenticating" @click="onAuthenticate">
                            <i v-if="isAuthenticating" class="fa fa-circle-notch fa-spin mr-1"></i>
                            {{ isAuthenticating ? 'Authenticating please wait...' : 'Authenticate' }}
                        </v-btn>

                        <v-btn v-if="hasOauth1" small @click="onAuthenticateOauth">
                            <i v-show="isAuthenticatingOauth" class="fa fa-circle-notch fa-spin mr-1"></i>
                            {{ isAuthenticatingOauth ? 'Authenticating please wait...' : 'Refresh authentication' }}
                        </v-btn>
                    </div>
                </div>
                <v-spacer />
                <v-btn class="mr-1" small @click="availableMetaDialog = true">
                    <v-icon left>mdi-information-outline</v-icon>
                    View all available settings
                </v-btn>
            </div>

            <v-dialog
            v-model="availableMetaDialog"
            max-width="80%"
            >
                <v-card>
                    <v-card-title>
                        <div class="text-h6">Available Settings</div>
                        <v-spacer />
                        <v-btn icon @click="availableMetaDialog = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-card-title>

                    <v-card-text>
                        <AvailableMetaFields :integrationId="integrationId" />
                    </v-card-text>
                </v-card>
            </v-dialog>

            <v-container fluid @keydown.enter.prevent>
                <div v-for="(meta, index) in integrationMetaFields" :key="meta.key + '_' + index">
                    <integrations-meta-form :ref="meta.key + '_' + index" :formMeta="meta" :platformMetaFields="platformMetaFields" :usedMetaKeys="usedMetaKeys">
                        <template v-slot:actions>
                            <span class="ic-remove clickable ml-auto" @click="onRemove(meta.key)">
                                <i class="fa fa-times-circle fa-2x"></i>
                            </span>
                        </template>
                    </integrations-meta-form>
                </div>

            </v-container>

            <div class="mt-3 d-flex">
                <v-spacer></v-spacer>
                <v-btn class="mr-1" small @click="onAddMore">Add Meta</v-btn>
                <v-btn small color="primary" @click="onSubmit" :disabled="isSaving">
                    <span v-if="isSaving">
                        <v-progress-circular
                        indeterminate
                            size="12"
                            width="2"
                        color="green darken-3">
                        </v-progress-circular>
                        saving
                    </span>
                    <span v-else>save</span>
                </v-btn>
            </div>
        </div>


    </div>
</template>

<script>
import { EventBus } from '@/js/event-bus.js'
import integrationsMetaForm from './integrationsMetaForm.vue';
import { MetaStruct } from './integrationsMetaStruct';
import AvailableMetaFields from '../modal/AvailableMetaFields.vue';

export default {

    components: {
        integrationsMetaForm,
        AvailableMetaFields
    },

    props: {
        hasOauth2: { type: Boolean, default: false },
        hasOauth1: { type: Boolean, default: false },
        integrationMeta: { type: Array, default: [] },
        integration: {type: Object, default: [] }
    },

    data() {
        return {
            integrationId: parseInt(this.$route.params.id),

            isLoading: true,
            isSaving: false,
            platformMetaFields: [],
            originalIntegrationMetaFields: [],
            integrationMetaFields: [],

            hasAuthenticate: this.hasOauth2 || this.hasOauth1, // has option to authenticate
            isAuthenticating: false,
            isAuthenticatingOauth: false,

            availableMetaDialog: false,
        }
    },

    computed: {
        usedMetaKeys() {
            return this.integrationMetaFields.map(meta => meta.key);
        }
    },

    created() {
        this.getPlatformMetaFields();
    },

    methods: {

        getPlatformMetaFields() {
            this.$http
                .get('/api/integrationmetas/available-meta-fields/' + this.$route.params.id)
                .then((data) => {
                    this.platformMetaFields = data.data;
                })
                .catch(err => {
                    EventBus.$emit('show-error', "Unable to load available meta configurations.");
                })
                .finally(() => {
                    this.setIntegrationMetaFields();
                    this.isLoading = false;
                });
        },

        setIntegrationMetaFields() {
            this.integrationMeta.forEach(meta => {
                // @todo: think of a better way to hide certain meta fields
                if (meta.meta_key.indexOf('LIVE_RATE') < 0 &&
                    meta.meta_key.indexOf('PACKING_GROUP_SETTINGS') < 0 &&
                    meta.meta_key.indexOf('INTEGRATION_NOTE') < 0 &&
                    meta.meta_key.indexOf('MS_ERROR_ACTIONS') < 0 &&
                    meta.meta_key.indexOf('MACHSHIP_WEBHOOK') < 0) {

                    const metaType = this.platformMetaFields.find(field => field.key === meta.meta_key)?.type || 'string';

                    let metaValue = meta.meta_value;

                    if (metaType == 'list') {
                        metaValue = meta.meta_value.split(',').filter(item => item.trim() !== '');
                    } else if (metaType == 'switch') {
                        metaValue = this.normalizeBoolean(meta.meta_value);
                    }

                    const platformMetaField = this.platformMetaFields.find(field => field.key === meta.meta_key);

                    this.integrationMetaFields.push(new MetaStruct({
                        id: meta.id,
                        key: meta.meta_key,
                        value: metaValue,
                        type: platformMetaField?.type || 'string',
                        objectSchema: platformMetaField?.objectSchema || null,
                        pattern: platformMetaField?.pattern || null,
                        hint: platformMetaField?.hint || '',
                        integration_id: meta.integration_id
                    }));
                }

                // set the original for comparison later, new instance to avoid reference issues
            });

            this.originalIntegrationMetaFields = JSON.parse(JSON.stringify(this.integrationMetaFields));

        },

        onAddMore() {

            this.integrationMetaFields.push(new MetaStruct({
                key: '',
                value: '',
                integration_id: this.integrationId
            }));
        },


        onRemove(key) {
            this.integrationMetaFields = this.integrationMetaFields.filter(
                meta => meta.key !== key
            );
        },

        onSubmit() {

            // Validate all child forms before submitting
            const children = [];
            this.integrationMetaFields.forEach((meta, index) => {
                children.push(this.$refs[meta.key + '_' + index]);
            });

            // Validate each child form
            const allValid = children.every(child => child[0].validate());
            if (!allValid) {
                EventBus.$emit('show-error', 'There are configurations with errors. Please fix them before saving.');
                return;
            }


            this.isSaving = true;

            this.$http
                .post('/api/integrationmetas/bulk-update/' + this.integrationId, {
                    before: this.originalIntegrationMetaFields,
                    after: this.integrationMetaFields
                })
                .then((result) => {
                    EventBus.$emit('show-success', 'All configurations have been successfully saved.')
                })
                .catch(err => {
                    EventBus.$emit('show-error', err);
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        onAuthenticate() {
            this.isAuthenticating = true
            window.location.href = '/oauth2/' + this.integrationId + '/init'
        },

        onAuthenticateOauth() {
            this.isAuthenticatingOauth = true;
            this.$http
                    .post('/api/integrations/myob-advanced-auth', {integrationId: this.integrationId})
                    .then((result) => {
                        let modal = result.data.error === false ? 'show-success': 'show-error';
                        let message = result.data.message ?? '';
                        this.isAuthenticatingOauth = false;

                        EventBus.$emit(modal, message);
                    })
                    .catch(err => {
                        this.isAuthenticatingOauth = false;
                        EventBus.$emit('show-error', err);
                    });
        },

        normalizeBoolean(value) {

            value = value.trim();

            if(value === '') return false;

            return ['1', 'true'].includes(value.toLowerCase()) ? "true" : "false";

            return false;
        }
    }
}

</script>